import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import '@/lib/i18n';
import { useCartSync } from '@/hooks/useCartSync';
import Index from "./pages/Index";
import Collections from "./pages/Collections";
import CollectionDetail from "./pages/CollectionDetail";
import About from "./pages/About";
import Cart from "./pages/Cart";
import Shop from "./pages/Shop";
import Product from "./pages/Product";
import Blog from "./pages/Blog";
import BlogArticle from "./pages/BlogArticle";
import NotFound from "./pages/NotFound";

/** Redirect /products/:handle → /product/:handle (old Shopify URLs indexed by Google) */
const ProductsRedirect = () => {
  const { handle } = useParams<{ handle: string }>();
  return <Navigate to={`/product/${handle}`} replace />;
};

/** Redirect /seo-catalog to the edge function for JS-enabled visitors */
const SeoCatalogRedirect = () => {
  window.location.href = 'https://zofxeobpwchjitqgfluv.supabase.co/functions/v1/seo-catalog';
  return null;
};

const queryClient = new QueryClient();

const CartSyncProvider = ({ children }: { children: React.ReactNode }) => {
  useCartSync();
  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <CartSyncProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/collections" element={<Collections />} />
            <Route path="/collections/:slug" element={<CollectionDetail />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/about" element={<About />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/product/:handle" element={<Product />} />
            {/* Redirect old Shopify /products/ URLs indexed by Google */}
            <Route path="/products/:handle" element={<ProductsRedirect />} />
            <Route path="/en/products/:handle" element={<ProductsRedirect />} />
            <Route path="/it/products/:handle" element={<ProductsRedirect />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/seo-catalog" element={<SeoCatalogRedirect />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </CartSyncProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
